import { useMemo } from 'react';

export type DynamicWidthSettings = {
  /**
   * Minimum number of characters in a label before dynamic width adjustment is triggered.
   * If no label exceeds this character count, the width will not be changed.
   */
  initialLabelCharacterThreshold: number;
  /**
   * The initial width to apply when the threshold is reached.
   * This width will be increased by `widthStepSize` for each step, up to `maxWidth`.
   */
  startingWidth: number;
  /**
   * The maximum width allowed for the component. The width will not exceed this value.
   */
  maxWidth: number;
  /**
   * The amount to increase the width by for each step above the threshold.
   */
  widthStepSize: number;
  /**
   * For every `labelCharacterStepSize` characters that the longest label exceeds
   * `initialLabelCharacterThreshold`, the width will be increased by `widthStepSize`,
   * up to the `maxWidth`.
   * Note: The first increment is done once the theshold is exceeded.
   */
  labelCharacterStepSize: number;
};

export const useDynamicWidth = (
  settings: DynamicWidthSettings | undefined,
  labels: string[]
): number | undefined => {
  const largestCharCount = useMemo(
    () =>
      labels
        .map((s) => s.length)
        .sort((a, b) => a - b)
        .at(-1),
    [labels]
  );

  return useMemo(() => {
    if (
      settings == null ||
      largestCharCount == null ||
      largestCharCount < settings.initialLabelCharacterThreshold
    ) {
      return;
    }

    const charCountAboveThreshold =
      largestCharCount - settings.initialLabelCharacterThreshold;

    const steps = Math.floor(
      charCountAboveThreshold / settings.labelCharacterStepSize + 1
    );

    return Math.min(
      settings.maxWidth,
      settings.startingWidth + steps * settings.widthStepSize
    );
  }, [largestCharCount, settings]);
};
